import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './BottomNav.css';

function BottomNav() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenuDrawer, setShowMenuDrawer] = useState(false);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // ドロワーが開いているときは背後スクロールを禁止
  useEffect(() => {
    if (showMenuDrawer || showUserPanel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showMenuDrawer, showUserPanel]);

  // 未読通知数をリアルタイム取得
  useEffect(() => {
    if (!currentUser) { setUnreadCount(0); return; }
    let unsubscribe;
    let active = true;
    (async () => {
      try {
        const { collection, query, where, onSnapshot } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const q = query(
          collection(db, 'notifications'),
          where('recipientId', '==', currentUser.uid),
          where('isRead', '==', false)
        );
        unsubscribe = onSnapshot(
          q,
          (snap) => {
            if (active) {
              setUnreadCount(snap.size);
            }
          },
          (error) => {
            // ログアウト直後などの permission-denied を未処理例外にしない
            if (error?.code !== 'permission-denied') {
              console.error('未読通知数の取得に失敗しました', error);
            }
            if (active) {
              setUnreadCount(0);
            }
          }
        );
      } catch (e) {
        setUnreadCount(0);
      }
    })();
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]);

  // ルート変更時にドロワーを閉じる
  useEffect(() => {
    setShowMenuDrawer(false);
    setShowUserPanel(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      setUnreadCount(0);
      await logout();
      navigate('/login');
    } catch (e) {
      console.error('ログアウトに失敗しました', e);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className="bottom-nav" aria-label="ボトムナビゲーション">
        {/* 1. メニュー */}
        <button
          className="bottom-nav__btn"
          onClick={() => { setShowUserPanel(false); setShowMenuDrawer(v => !v); }}
          aria-label="メニュー"
        >
          <span className="bottom-nav__icon">
            <span className={`bottom-hamburger ${showMenuDrawer ? 'active' : ''}`}>
              <span /><span /><span />
            </span>
          </span>
          <span className="bottom-nav__label">メニュー</span>
        </button>


        {/* 3. 募集を探す（メイン機能） */}
        <button
          className={`bottom-nav__btn bottom-nav__btn--main ${isActive('/recruitments') ? 'bottom-nav__btn--active' : ''}`}
          onClick={() => navigate('/recruitments')}
          aria-label="募集を探す"
        >
          <span className="bottom-nav__icon">🔍</span>
          <span className="bottom-nav__label">募集を探す</span>
        </button>

        {/* 4. チャット */}
        <button
          className={`bottom-nav__btn ${isActive('/chat') ? 'bottom-nav__btn--active' : ''}`}
          onClick={() => navigate('/chats')}
          aria-label="チャット"
        >
          <span className="bottom-nav__icon">💬</span>
          <span className="bottom-nav__label">チャット</span>
        </button>

        {/* 5. マイページ（PCサイドバー相当） */}
        <button
          className="bottom-nav__btn"
          onClick={() => { setShowMenuDrawer(false); setShowUserPanel(v => !v); }}
          aria-label="マイページ"
        >
          <span className="bottom-nav__icon">
            👤
            {unreadCount > 0 && <span className="bottom-nav__badge">{unreadCount}</span>}
          </span>
          <span className="bottom-nav__label">マイページ</span>
        </button>
      </nav>

      {/* ── メニュードロワー ── */}
      {showMenuDrawer && (
        <div className="bottom-drawer-overlay" onClick={() => setShowMenuDrawer(false)}>
          <div className="bottom-drawer" onClick={e => e.stopPropagation()}>
            <div className="bottom-drawer__header">
              <span className="bottom-drawer__title">メニュー</span>
              <button className="bottom-drawer__close" onClick={() => setShowMenuDrawer(false)}>✕</button>
            </div>
            <ul className="bottom-drawer__list">
              <li>
                <Link to="/recruitments" className="bottom-drawer__item">
                  <span>🔍</span><span>募集を探す</span>
                </Link>
              </li>
              {currentUser && (
                <li>
                  <Link to="/create-recruitment" className="bottom-drawer__item">
                    <span>✏️</span><span>募集を作る</span>
                  </Link>
                </li>
              )}
              {currentUser && (
                <li>
                  <Link to="/history" className="bottom-drawer__item">
                    <span>📋</span><span>応募履歴</span>
                  </Link>
                </li>
              )}
              {currentUser && (
                <li>
                  <Link to="/my-recruitments" className="bottom-drawer__item">
                    <span>📝</span><span>募集履歴</span>
                  </Link>
                </li>
              )}
              {currentUser && (
                <li>
                  <Link to="/chats" className="bottom-drawer__item">
                    <span>💬</span><span>チャット</span>
                  </Link>
                </li>
              )}
              {currentUser && (
                <li>
                  <Link to="/notifications" className="bottom-drawer__item">
                    <span>🔔</span>
                    <span>通知{unreadCount > 0 && <span className="bottom-drawer__badge">{unreadCount}</span>}</span>
                  </Link>
                </li>
              )}
              <li>
                <Link to="/mountain-log" className="bottom-drawer__item">
                  <span>⛰️</span><span>登頂記録</span>
                </Link>
              </li>
              <li>
                <Link to="/fare-split" className="bottom-drawer__item">
                  <span>🚗</span><span>交通費ワリカン</span>
                </Link>
              </li>
              <li>
                <Link to="/mountain-type" className="bottom-drawer__item">
                  <span>🧭</span><span>登山タイプ診断</span>
                </Link>
              </li>
              <li>
                <Link to="/contact" className="bottom-drawer__item">
                  <span>✉</span><span>問合せ・不具合</span>
                </Link>
              </li>
              <li>
                <a href="https://yamano-media.com/blog/mountain-checker-app/" className="bottom-drawer__item" target="_blank" rel="noopener noreferrer">
                  <span>🎒</span><span>登山装備チェック</span>
                </a>
              </li>
              <li>
                <a href="https://yamano-media.com/blog/trek-cal/" className="bottom-drawer__item" target="_blank" rel="noopener noreferrer">
                  <span>🔥</span><span>行動食計算</span>
                </a>
              </li>
              <li>
                <a href="https://yamano-media.com/blog/word/" className="bottom-drawer__item" target="_blank" rel="noopener noreferrer">
                  <span>📖</span><span>山の言葉辞典</span>
                </a>
              </li>
              {currentUser && (
                <li>
                  <Link to="/achievements" className="bottom-drawer__item">
                    <span>🏅</span><span>実績一覧</span>
                  </Link>
                </li>
              )}
              {!currentUser && (
                <>
                  <li>
                    <Link to="/login" className="bottom-drawer__item">
                      <span>🔐</span><span>ログイン</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/signup" className="bottom-drawer__item">
                      <span>📝</span><span>新規登録</span>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* ── マイページパネル（PCサイドバー相当） ── */}
      {showUserPanel && (
        <div className="bottom-drawer-overlay" onClick={() => setShowUserPanel(false)}>
          <div className="bottom-drawer" onClick={e => e.stopPropagation()}>
            <div className="bottom-drawer__header">
              <span className="bottom-drawer__title">
                {currentUser ? (currentUser.displayName || 'マイページ') : 'アカウント'}
              </span>
              <button className="bottom-drawer__close" onClick={() => setShowUserPanel(false)}>✕</button>
            </div>
            {currentUser ? (
              <ul className="bottom-drawer__list">
                <li>
                  <Link to="/profile/edit" className="bottom-drawer__item">
                    <span>👤</span><span>プロフィール編集</span>
                  </Link>
                </li>
                <li>
                  <Link to="/history" className="bottom-drawer__item">
                    <span>📋</span><span>応募履歴</span>
                  </Link>
                </li>
                <li>
                  <Link to="/my-recruitments" className="bottom-drawer__item">
                    <span>📝</span><span>募集履歴</span>
                  </Link>
                </li>
                <li>
                  <Link to="/chats" className="bottom-drawer__item">
                    <span>💬</span><span>チャット一覧</span>
                  </Link>
                </li>
                <li>
                  <Link to="/notifications" className="bottom-drawer__item">
                    <span>🔔</span>
                    <span>通知{unreadCount > 0 && <span className="bottom-drawer__badge">{unreadCount}</span>}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/achievements" className="bottom-drawer__item">
                    <span>🏅</span><span>実績一覧</span>
                  </Link>
                </li>
                <li className="bottom-drawer__divider" />
                <li>
                  <button className="bottom-drawer__item bottom-drawer__item--danger" onClick={handleLogout}>
                    <span>🚪</span><span>ログアウト</span>
                  </button>
                </li>
              </ul>
            ) : (
              <ul className="bottom-drawer__list">
                <li>
                  <Link to="/login" className="bottom-drawer__item">
                    <span>🔐</span><span>ログイン</span>
                  </Link>
                </li>
                <li>
                  <Link to="/signup" className="bottom-drawer__item">
                    <span>📝</span><span>新規登録</span>
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default BottomNav;
